import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { isDefined, pluralize } from '@terros/common'

export const toAbbreviatedCount = (
  count: number,
  options?: { skipThousand?: boolean; skipMillion?: boolean; skipBillion?: boolean }
): string => {
  if (count < 1_000) return Math.round(count).toLocaleString()

  if (count < 1_000_000) {
    if (options?.skipThousand) return Math.round(count).toLocaleString()
    return toK(count)
  }
  if (count < 1_000_000_000) {
    if (options?.skipMillion) return toK(count)
    return toM(count)
  }
  if (options?.skipBillion) return toM(count)
  return toB(count)
}

const toK = (count: number): string => {
  const decimalPoints = count < 100_000 ? 1 : 0
  return `${Number((count / 1_000).toFixed(decimalPoints)).toLocaleString()}K`
}

const toM = (count: number): string => {
  const countInMillion = count / 1_000_000
  const decimalPoints = countInMillion < 10 ? 1 : 0
  return `${Number(countInMillion.toFixed(decimalPoints)).toLocaleString()}M`
}

const toB = (count: number): string => {
  const countInBillion = count / 1_000_000_000
  const decimalPoints = countInBillion < 10 ? 1 : 0
  return `${Number(countInBillion.toFixed(decimalPoints)).toLocaleString()}B`
}

export const toShowingCountText = (filtered: number, total: number, name: string, namePlural?: string): string => {
  if (filtered === total) {
    return `${total.toLocaleString()} ${pluralize(total, name, namePlural)}`
  }
  return `Showing ${filtered.toLocaleString()} of ${total.toLocaleString()} ${pluralize(total, name, namePlural)}`
}

export const toValueInRange = (value: number, min: number, max: number): number => {
  return Math.max(Math.min(value, max), min)
}

export const toValueInOptionalRange = (value: number, min?: number, max?: number): number => {
  if (isDefined(min) && isDefined(max)) {
    return toValueInRange(value, min, max)
  }
  if (isDefined(min)) return Math.max(value, min)
  if (isDefined(max)) return Math.min(value, max)
  return value
}

export const toRoundedValue = (value: number, precision: number = 2): number => {
  return Number(value.toFixed(precision))
}

export const toDisplayPercentageValue = (value: number): string => {
  const precision = value < 10 ? 2 : 0
  return `${toRoundedValue(value, precision)}%`
}

export const isInRange = (value: number, min: number, max: number, inclusive: boolean = true): boolean => {
  if (inclusive) {
    return value >= min && value <= max
  }
  return value > min && value < max
}

export const useDisplayPercentageValue = (): ((value: number) => string) => {
  const { t } = useTranslation()
  const toDisplayPercentageText = useCallback(
    (value: number) => {
      if (isNaN(value)) return t('N/A')
      return toDisplayPercentageValue(value)
    },
    [t]
  )
  return toDisplayPercentageText
}
